import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarApi, projectsApi } from '../api/client';
import {
  X,
  Calendar,
  Link2,
  Copy,
  Check,
  Trash2,
  Plus,
  Folder,
  CheckCircle2,
} from 'lucide-react';
import { showToast } from './Toaster';

interface CalendarSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CalendarSubscriptionModal({ isOpen, onClose }: CalendarSubscriptionModalProps) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [newSubscription, setNewSubscription] = useState({
    name: '',
    projectFilter: '',
    includeCompleted: false,
  });

  // 获取订阅列表
  const { data: subscriptionsData } = useQuery({
    queryKey: ['calendar-subscriptions'],
    queryFn: () => calendarApi.getSubscriptions(),
    enabled: isOpen,
  });

  // 获取项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
    enabled: isOpen,
  });

  const subscriptions = subscriptionsData?.success ? subscriptionsData.data : [];
  const projects = projectsData?.success ? projectsData.data : [];

  // 创建订阅
  const createMutation = useMutation({
    mutationFn: (data: typeof newSubscription) => calendarApi.createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-subscriptions'] });
      setIsCreating(false);
      setNewSubscription({ name: '', projectFilter: '', includeCompleted: false });
      showToast('日历订阅创建成功', 'success');
    },
    onError: () => {
      showToast('创建失败，请重试', 'error');
    },
  });

  // 删除订阅
  const deleteMutation = useMutation({
    mutationFn: (id: string) => calendarApi.deleteSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-subscriptions'] });
      showToast('订阅已删除', 'success');
    },
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSubscription.name) return;
    await createMutation.mutateAsync(newSubscription);
  };

  const handleCopyUrl = (token: string) => {
    const url = calendarApi.getIcsUrl(token);
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    showToast('链接已复制到剪贴板', 'success');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleCopyGuide = () => {
    const guide = `iOS日历订阅设置指南：

1. 复制上方的ICS链接
2. 打开iPhone的"设置" > "日历" > "账户" > "添加账户"
3. 选择"其他" > "添加已订阅的日历"
4. 粘贴链接，点击"下一步"
5. 输入描述（如"个人Life OS"）
6. 点击"存储"

时区：东京标准时间 (Asia/Tokyo)
提醒：根据任务设置自动同步`;

    navigator.clipboard.writeText(guide);
    showToast('设置指南已复制', 'success');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            日历订阅管理
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 内容 */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
          {/* iOS设置指南 */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-2">iOS日历订阅</h3>
            <p className="text-sm text-blue-700 mb-3">
              创建订阅后，复制链接到iPhone日历应用中订阅，任务提醒将自动同步。
            </p>
            <button
              onClick={handleCopyGuide}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              复制设置指南
            </button>
          </div>

          {/* 创建新订阅 */}
          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all"
            >
              <Plus className="w-5 h-5" />
              创建新订阅
            </button>
          ) : (
            <form onSubmit={handleCreate} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  订阅名称
                </label>
                <input
                  type="text"
                  value={newSubscription.name}
                  onChange={(e) =>
                    setNewSubscription({ ...newSubscription, name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例如：工作日历"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  项目过滤
                </label>
                <div className="relative">
                  <Folder className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={newSubscription.projectFilter}
                    onChange={(e) =>
                      setNewSubscription({
                        ...newSubscription,
                        projectFilter: e.target.value,
                      })
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">所有项目</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeCompleted"
                  checked={newSubscription.includeCompleted}
                  onChange={(e) =>
                    setNewSubscription({
                      ...newSubscription,
                      includeCompleted: e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="includeCompleted" className="text-sm text-gray-700">
                  包含已完成的任务
                </label>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="flex-1 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-all"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !newSubscription.name}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all"
                >
                  {createMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  创建
                </button>
              </div>
            </form>
          )}

          {/* 订阅列表 */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">我的订阅</h3>
            {subscriptions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">暂无订阅</p>
            ) : (
              subscriptions.map((subscription) => (
                <div
                  key={subscription.id}
                  className="p-4 bg-gray-50 rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{subscription.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {subscription.projectFilter
                          ? projects.find((p) => p.id === subscription.projectFilter)?.name ||
                            '未知项目'
                          : '所有项目'}
                        {subscription.includeCompleted && ' · 包含已完成'}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteMutation.mutate(subscription.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* ICS链接 */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={calendarApi.getIcsUrl(subscription.token)}
                        readOnly
                        className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-600"
                      />
                    </div>
                    <button
                      onClick={() => handleCopyUrl(subscription.token)}
                      className={`p-2 rounded-lg transition-all ${
                        copiedToken === subscription.token
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      {copiedToken === subscription.token ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
